import React, { useEffect, useState } from 'react';
import { drawerSections } from './content';
import { cloneDeep } from 'lodash';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  setBusinessPartnerPlanCommission,
  getDefaultCommissionEnums,
} from './queries';
import { LookupToList } from './utils';
import { EnhancedButtonStatus } from '@components/_old/EnhancedButton';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { getError } from '@utils/graph-utils';
import { trimTrailingZeros } from '@utils/formatting-utils';
import { ISalesforceBusinessPartnerCommissionInfo } from '.';
import { isEmpty } from '@utils/validationUtils';
import PrecisionService from '@services/PrecisionService';
import DateService from '@services/DateService';
import { IDetailedSelection } from '../../../..';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { ISelectFormFieldProps } from '@components/dynamic-form/types/inputTypes';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';

interface ICommissionDrawerProps {
  businessPartnerId: string;
  selectedPlans: string[];
  selectedDetailedSelection: IDetailedSelection;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CommissionDrawer: React.FC<ICommissionDrawerProps> = ({
  businessPartnerId,
  selectedPlans,
  selectedDetailedSelection,
  open,
  onSuccess,
  onClose,
}) => {
  const [booted, setBooted] = useState<boolean>(false);

  const [defaultCommissionEnumResults] = useLazyQuery(
    getDefaultCommissionEnums()
  );

  const [setBusinessPartnerPlanCommissionAction] = useMutation(
    setBusinessPartnerPlanCommission()
  );

  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [inputsForm, setInputsForm] =
    useState<IDynamicFormWidgetSection[]>(drawerSections);

  const loadLovList = async () => {
    const result = await defaultCommissionEnumResults({
      fetchPolicy: 'no-cache',
    });

    const list = LookupToList(result.data);
    return list;
  };

  const getEntityInfo = () => {
    const isSingleSelection = selectedPlans.length === 1;

    let defaultCommissionEntity: Record<string, any> = {
      commissionType: 'RATE',
      lineCommissionValue: '0',
      lineCommissionValueCurrency: '39735',
      lineCommissionRate: '0',
      lineCommissionApplicableOn: '',
      activeFrom: '',
      activeTo: '',
    };

    if (isSingleSelection) {
      const type = !isEmpty(
        selectedDetailedSelection?.businessPartnerCommissionType
      )
        ? selectedDetailedSelection?.businessPartnerCommissionType
        : selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_Plan_SpecialCommissionApplicable ===
            true
          ? selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_Plan_SpecialCommissionType.toUpperCase()
          : !isEmpty(
                selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID_LineCommissionType
              )
            ? selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID_LineCommissionType
            : 'RATE';
      const rate = !isEmpty(
        selectedDetailedSelection?.businessPartnerCommissionRate
      )
        ? PrecisionService.multiplyBy100(
            selectedDetailedSelection?.businessPartnerCommissionRate
          )
        : selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_Plan_SpecialCommissionApplicable ===
            true
          ? PrecisionService.multiplyBy100(
              selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_Plan_SpecialCommissionRate
            )
          : !isEmpty(
                selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID_LineCommissionRate
              )
            ? PrecisionService.multiplyBy100(
                selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID_LineCommissionRate
              )
            : 0;
      const value = !isEmpty(
        selectedDetailedSelection?.businessPartnerCommissionValue
      )
        ? selectedDetailedSelection?.businessPartnerCommissionValue
        : selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_Plan_SpecialCommissionApplicable ===
            true
          ? selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_Plan_SpecialCommissionValue
          : !isEmpty(
                selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID_LineCommissionValue
              )
            ? selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID_LineCommissionValue
            : 0;

      const valueCurrency = !isEmpty(
        selectedDetailedSelection?.businessPartnerCommissionValueCurrency
      )
        ? selectedDetailedSelection?.businessPartnerCommissionValueCurrency
        : selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_Plan_SpecialCommissionApplicable ===
            true
          ? selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_Plan_SpecialCommissionValueCurrency
          : !isEmpty(
                selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID_ValueCurrency
              )
            ? selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID_ValueCurrency
            : '39735';
      const applicableOn = !isEmpty(
        selectedDetailedSelection?.businessPartnerCommissionApplicableOn
      )
        ? selectedDetailedSelection?.businessPartnerCommissionApplicableOn
        : selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_Plan_SpecialCommissionApplicable ===
            true
          ? selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_Plan_SpecialCommissionApplicableOn
          : !isEmpty(
                selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID_ApplicableOn
              )
            ? selectedDetailedSelection?.salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID_ApplicableOn
            : '';
      const activeFrom = !isEmpty(
        selectedDetailedSelection?.businessPartnerActiveFrom
      )
        ? selectedDetailedSelection?.businessPartnerActiveFrom
        : '';
      const activeTo = !isEmpty(
        selectedDetailedSelection?.businessPartnerActiveTo
      )
        ? selectedDetailedSelection?.businessPartnerActiveTo
        : '';

      defaultCommissionEntity = {
        commissionType: type?.toUpperCase(),
        lineCommissionValue: trimTrailingZeros(value),
        lineCommissionValueCurrency: valueCurrency,
        lineCommissionRate: trimTrailingZeros(rate),
        lineCommissionApplicableOn: applicableOn,
        activeFrom: activeFrom,
        activeTo: activeTo,
      };
    }
    return defaultCommissionEntity;
  };

  const initialize = async () => {
    try {
      const updatedSections = cloneDeep(inputsForm);
      const updatedInputs = updatedSections[0].inputs;
      const lovResult = await loadLovList();
      (updatedInputs.commissionType as ISelectFormFieldProps).selectOptions =
        lovResult['commissionTypes'];
      (
        updatedInputs.lineCommissionApplicableOn as ISelectFormFieldProps
      ).selectOptions = lovResult['commissionApplicableOnOptions'];

      const entityData =
        getEntityInfo() as ISalesforceBusinessPartnerCommissionInfo;

      updatedInputs.commissionType.value = entityData.commissionType;
      updatedInputs.lineCommissionValue.value = entityData.lineCommissionValue;
      updatedInputs.lineCommissionValueCurrency.value =
        entityData.lineCommissionValueCurrency;
      updatedInputs.lineCommissionRate.value = entityData.lineCommissionRate;
      updatedInputs.lineCommissionApplicableOn.value =
        entityData.lineCommissionApplicableOn;
      updatedInputs.activeFrom.value = entityData.activeFrom;
      updatedInputs.activeTo.value = entityData.activeTo;

      setInputsForm(updatedSections);
      setBooted(true);
    } catch (e) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(drawerSections, values);

    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      const isRate = data.commissionType.toLowerCase() === 'rate';

      const variables = {
        selectedBusinessPartnerPlanIds: selectedPlans,
        businessPartnerPlanInputs: {
          activeFrom: DateService.formatDateBackend(data.activeFrom),
          activeTo: DateService.formatDateBackend(data.activeTo),
          planCommissionApplicableOn: isRate
            ? data.lineCommissionApplicableOn || null
            : null,
          planCommissionRate: isRate
            ? PrecisionService.divideBy100(data.lineCommissionRate)
            : 0,
          planCommissionType: data.commissionType,
          planCommissionValue: !isRate ? Number(data.lineCommissionValue) : 0,
          planCommissionValueCurrency: !isRate
            ? data.lineCommissionValueCurrency
            : '39735',
        },
      };

      const res = await setBusinessPartnerPlanCommissionAction({
        variables: { ...variables, entityId: businessPartnerId },
        errorPolicy: 'all',
      });

      if (isEmpty(res.errors)) {
        toast.success(
          <ToastSuccessMessage>
            {'Commissions successfully updated.'}
          </ToastSuccessMessage>
        );
        setTimeout(() => {
          setSubmitButtonState('success');
          onSuccess();
          onClose();
          setFormDisabled(false);
        }, 500);
      } else {
        setFormDisabled(false);
        setSubmitButtonState(undefined);
        toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
      }
    } catch (err) {
      setFormDisabled(false);
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      //
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  return (
    <DynamicFormDrawer
      title={'Set Commission'}
      onClose={() => onClose()}
      isOpen={open}
      loading={!booted}
      sections={inputsForm}
      onSubmit={(values) => submitForm(values)}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
    />
  );
};

export default CommissionDrawer;
